import { computed, ref, watch, type Ref } from "vue";
import { useUser } from "@/composables/user";
import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { OwnedCountByFoil } from "@/services/collectibles/foilVariants";
import { fetchCollectibleOwnedCounts } from "@/services/collectibles/ownedCounts/ownedCountsApi";
import { CollectibleOwnershipStatus } from "@/services/collectibles/ownedCounts/ownedCountsSchema";
import type { CollectibleOwnedCount } from "@/services/collectibles/ownedCounts/ownedCountsTypes";
import type { UserId } from "@/services/user/userTypes";

/** The backend rejects requests with more ids (400). */
const MaxCollectibleIdsPerRequest = 500;

/**
 * Session cache of per-collectible owned/listed counts. Ids the user owns nothing of are cached
 * as explicit zero entries, so a missing key means "not loaded yet", never "owns nothing".
 */
const ownedCountsCache = new Map<CollectibleId, CollectibleOwnedCount>();
/** The cache holds one user's counts only; a different logged-in user drops it. */
let cacheOwnerUserId: UserId | undefined;
/** Bumped on every cache write/clear so all mounted lists rebuild (or refetch) their maps. */
const cacheVersion = ref(0);

const zeroEntry = (collectibleId: CollectibleId): CollectibleOwnedCount => ({
  collectibleId,
  status: CollectibleOwnershipStatus.NotInCollection,
  ownedCount: 0,
  ownedCountByVariant: new Map(),
  onMarketplace: 0,
  onMarketplaceByVariant: new Map(),
});

/** Listing/unlisting moves pieces between collection and marketplace — drop everything; mounted lists refetch lazily. */
export function clearCollectibleOwnedCountsCache(): void {
  ownedCountsCache.clear();
  cacheVersion.value++;
}

const deriveStatus = (ownedCount: number, onMarketplace: number): CollectibleOwnershipStatus => {
  if (ownedCount > 0) return CollectibleOwnershipStatus.InCollection;
  if (onMarketplace > 0) return CollectibleOwnershipStatus.OnMarketplace;
  return CollectibleOwnershipStatus.NotInCollection;
};

/** Optimistic write-back after a foil-stepper edit: replace collection-side counts, keep marketplace-side counts. */
export function setOwnedCountsInCache(collectibleId: CollectibleId, counts: OwnedCountByFoil): void {
  const previous = ownedCountsCache.get(collectibleId) ?? zeroEntry(collectibleId);
  const ownedCount = [...counts.values()].reduce((sum, count) => sum + count, 0);
  ownedCountsCache.set(collectibleId, {
    ...previous,
    status: deriveStatus(ownedCount, previous.onMarketplace),
    ownedCount,
    ownedCountByVariant: new Map(counts),
  });
  cacheVersion.value++;
}

/**
 * Best-effort ownership overlay for a list of collectibles, loaded lazily from the batch
 * owned-count endpoint. The map is empty when logged out and entries are missing until loaded
 * or on failure — callers render placeholders and the failed ids are retried on the next list.
 */
export function useCollectibleOwnedCounts(collectibleIds: Ref<CollectibleId[]>) {
  const { isUserLoggedIn, userId } = useUser();

  const ownedCountsByCollectibleId = ref<Map<CollectibleId, CollectibleOwnedCount>>(new Map());

  const rebuildFromCache = () => {
    const map = new Map<CollectibleId, CollectibleOwnedCount>();
    collectibleIds.value.forEach((id) => {
      const entry = ownedCountsCache.get(id);
      if (entry !== undefined) {
        map.set(id, entry);
      }
    });
    ownedCountsByCollectibleId.value = map;
  };

  const load = async (ids: CollectibleId[]) => {
    if (cacheOwnerUserId !== userId.value) {
      ownedCountsCache.clear();
      cacheOwnerUserId = userId.value;
    }
    const uncachedIds = [...new Set(ids)].filter((id) => !ownedCountsCache.has(id)).slice(0, MaxCollectibleIdsPerRequest);
    if (uncachedIds.length === 0) {
      rebuildFromCache();
      return;
    }
    try {
      const response = await fetchCollectibleOwnedCounts(uncachedIds);
      const entriesById = new Map(response.items.map((item) => [item.collectibleId, item]));
      // Requested ids omitted from the response are owned nowhere — cache the zero entry too.
      uncachedIds.forEach((id) => ownedCountsCache.set(id, entriesById.get(id) ?? zeroEntry(id)));
    } catch {
      // Best-effort overlay: render placeholders now, retry the failed ids on the next list.
    }
    rebuildFromCache();
  };

  watch(
    computed(() => `${isUserLoggedIn.value}|${cacheVersion.value}|${[...new Set(collectibleIds.value)].sort((a, b) => a - b).join(",")}`),
    () => {
      if (isUserLoggedIn.value && collectibleIds.value.length > 0) {
        void load(collectibleIds.value);
      } else {
        ownedCountsByCollectibleId.value = new Map();
      }
    },
    { immediate: true }
  );

  return { ownedCountsByCollectibleId };
}
